import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { AppLayout } from '@/components/layout/AppLayout'
import { ToastContainer } from '@/components/ui/Toast'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { AthletesPage } from '@/pages/athletes/AthletesPage'
import { GroupsPage } from '@/pages/groups/GroupsPage'
import { CoachesPage } from '@/pages/coaches/CoachesPage'
import { CalendarPage } from '@/pages/calendar/CalendarPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import { MaterialsPage } from '@/pages/materials/MaterialsPage'
import { PaymentsPage } from '@/pages/payments/PaymentsPage'
import { EventsPage } from '@/pages/events/EventsPage'
import { AttendancePage } from '@/pages/attendance/AttendancePage'
import { AnalyticsPage } from '@/pages/analytics/AnalyticsPage'
import { ActivityPage } from '@/pages/activity/ActivityPage'
import { ClubPage } from '@/pages/club/ClubPage'
import { AIAssistantPage } from '@/pages/ai/AIAssistantPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/athletes" element={<AthletesPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/coaches" element={<CoachesPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/materials" element={<MaterialsPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/club" element={<ClubPage />} />
            <Route path="/ai" element={<AIAssistantPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastContainer />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
